import React, { useId, useState } from 'react';
import { Mail, MessageSquare } from 'lucide-react';
import styles from './SendMessageStep.module.css';
import { EmailSetup } from './email-setup';
import { SMSSetup } from './SMSSetup';
import type { SendMessageFormField, SenderDetailsUpdate } from './types';

type Tab = 'email' | 'sms';

interface SendMessageFormProps {
  subject: string;
  previewText: string;
  html: string;
  css: string;
  text: string;
  onChange: (field: SendMessageFormField, value: string | boolean) => void;
  senderProfileId: string | null | undefined;
  usePointOfContactSender?: boolean;
  onSenderProfileChange: (update: SenderDetailsUpdate) => void;
}

export const SendMessageForm: React.FC<SendMessageFormProps> = ({
  subject,
  previewText,
  html,
  css,
  text,
  onChange,
  senderProfileId,
  usePointOfContactSender,
  onSenderProfileChange,
}) => {
  const [activeTab, setActiveTab] = useState<Tab>('email');
  const id = useId();
  const emailTabId = `${id}-tab-email`;
  const smsTabId = `${id}-tab-sms`;
  const panelId = `${id}-panel`;

  return (
    <div className={styles.sendMessageForm}>
      <div className={styles.tabBar} role="tablist">
        <button
          type="button"
          role="tab"
          id={emailTabId}
          aria-selected={activeTab === 'email'}
          aria-controls={panelId}
          className={styles.tab}
          onClick={() => setActiveTab('email')}>
          <Mail />
          Email
        </button>
        <button
          type="button"
          role="tab"
          id={smsTabId}
          aria-selected={activeTab === 'sms'}
          aria-controls={panelId}
          className={styles.tab}
          onClick={() => setActiveTab('sms')}>
          <MessageSquare />
          SMS
        </button>
      </div>
      <div
        id={panelId}
        role="tabpanel"
        aria-labelledby={activeTab === 'email' ? emailTabId : smsTabId}
        className={styles.tabContent}>
        {activeTab === 'email' && (
          <EmailSetup
            subject={subject}
            previewText={previewText}
            html={html}
            css={css}
            onSubjectChange={value => onChange('subject', value)}
            onPreviewTextChange={value => onChange('previewText', value)}
            senderProfileId={senderProfileId}
            usePointOfContactSender={usePointOfContactSender}
            onSenderProfileChange={onSenderProfileChange}
          />
        )}
        {activeTab === 'sms' && (
          <SMSSetup defaultValue={text} onChange={value => onChange('text', value)} />
        )}
      </div>
    </div>
  );
};
